import { AxiosError } from 'axios';

export interface AppError {
  message: string;
  code?: string;
  details?: any;
}

/**
 * Extract user-friendly error message from various error types
 */
export const getErrorMessage = (error: unknown): string => {
  // Handle Axios errors
  if (error instanceof AxiosError) {
    if (error.response) {
      // Server responded with error
      const data = error.response.data;
      
      // Check for specific error messages
      if (data?.error?.message) return data.error.message;
      if (data?.error) return typeof data.error === 'string' ? data.error : 'Server error occurred';
      if (data?.message) return data.message;
      
      // Handle specific status codes
      switch (error.response.status) {
        case 401:
          return 'Your session has expired. Please log in again.';
        case 403:
          return 'You do not have permission to perform this action.';
        case 404:
          return 'The requested resource was not found.';
        case 429:
          return 'Too many requests. Please wait a moment and try again.';
        case 500:
          return 'Internal server error. Please try again later.';
        default:
          return `Server error: ${error.response.status}`;
      }
    } else if (error.request) {
      // Request made but no response
      return 'Unable to connect to the server. Please check your internet connection.';
    }
    // Error in request setup
    return error.message || 'An error occurred while making the request.';
  }
  
  // Handle Supabase errors
  if (error && typeof error === 'object' && 'message' in error) {
    const supabaseError = error as { message: string; code?: string };
    
    // Map common Supabase error codes to user-friendly messages
    switch (supabaseError.code) {
      case 'invalid_credentials':
        return 'Invalid email or password.';
      case 'email_not_confirmed':
        return 'Please verify your email address before logging in.';
      case 'user_already_exists':
        return 'An account with this email already exists.';
      case 'weak_password':
        return 'Password is too weak. Please use a stronger password.';
      case 'invalid_jwt':
      case 'session_not_found':
        return 'Your session has expired. Please log in again.';
      case 'email_change_needs_reauthentication':
        return 'Please log in again to change your email.';
      default:
        return supabaseError.message || 'An error occurred.';
    }
  }
  
  // Handle standard Error objects
  if (error instanceof Error) {
    return error.message;
  }
  
  // Handle string errors
  if (typeof error === 'string') {
    return error;
  }
  
  // Fallback
  return 'An unexpected error occurred. Please try again.';
};

/**
 * Check if error is an authentication error
 */
export const isAuthError = (error: unknown): boolean => {
  if (error instanceof AxiosError) {
    return error.response?.status === 401;
  }
  
  if (error && typeof error === 'object' && 'code' in error) {
    const code = (error as any).code;
    return ['invalid_jwt', 'session_not_found', 'invalid_credentials'].includes(code);
  }
  
  return false;
};

/**
 * Check if error is a network error
 */
export const isNetworkError = (error: unknown): boolean => {
  if (error instanceof AxiosError) {
    return !error.response && !!error.request;
  }
  
  if (error instanceof Error) {
    return error.message.toLowerCase().includes('network') || 
           error.message.toLowerCase().includes('internet');
  }
  
  return false;
};
